import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, MapPin, Globe, Clock, Video, Calendar, AlertTriangle, ChevronLeft, Flag, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet-async';

const API = process.env.REACT_APP_BACKEND_URL;
const EXP_LABELS = { benevole: 'Bénévole', professionnel: 'Professionnel', expert_certifie: 'Expert certifié' };
const INTERVENTION_LABELS = { distanciel: 'Distanciel', presentiel: 'Présentiel', les_deux: 'Distanciel & Présentiel' };
const VISIO_LABELS = { zoom: 'Zoom', google_meet: 'Google Meet', microsoft_teams: 'Microsoft Teams', autre: 'Autre' };

export default function MentorProfile() {
  const { mentorId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mentor, setMentor] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingOffer, setBookingOffer] = useState(null);
  const [bookMsg, setBookMsg] = useState('');
  const [booking, setBooking] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [showReport, setShowReport] = useState(false);
  const [report, setReport] = useState({ reason: '', description: '' });

  useEffect(() => {
    const load = async () => {
      try {
        const [mRes, rRes] = await Promise.all([
          axios.get(`${API}/api/mentors/${mentorId}`),
          axios.get(`${API}/api/mentors/${mentorId}/reviews`),
        ]);
        setMentor(mRes.data);
        setReviews(rRes.data.reviews || []);
      } catch { navigate('/mentors'); }
      finally { setLoading(false); }
    };
    load();
  }, [mentorId, navigate]);

  const handleBook = async (offer) => {
    if (!user) { toast.error('Connectez-vous pour réserver'); navigate('/auth'); return; }
    setBooking(true);
    try {
      const res = await axios.post(`${API}/api/mentors/${mentorId}/book`, { offer_id: offer.id, message: bookMsg }, { withCredentials: true });
      if (res.data.calendly_url) {
        toast.success('Redirection vers Calendly...');
        setTimeout(() => window.open(res.data.calendly_url, '_blank'), 1000);
      } else {
        toast.success('Demande envoyée ! Le mentor vous contactera.');
      }
      setBookingOffer(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBooking(false); }
  };

  const handleReview = async () => {
    try {
      await axios.post(`${API}/api/mentors/${mentorId}/reviews`, newReview, { withCredentials: true });
      toast.success('Avis publié !');
      setShowReview(false);
      const r = await axios.get(`${API}/api/mentors/${mentorId}/reviews`);
      setReviews(r.data.reviews || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleReport = async () => {
    await axios.post(`${API}/api/mentors/${mentorId}/report`, report, { withCredentials: true });
    toast.success('Signalement envoyé. Notre équipe va examiner ce profil.');
    setShowReport(false);
  };

  if (loading) return <Layout><div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-[#ff6b6b] border-t-transparent animate-spin" /></div></Layout>;
  if (!mentor) return null;

  return (
    <Layout>
      <Helmet>
        <title>{mentor.name} — Mentor Kurso</title>
        <meta name="description" content={`${mentor.bio?.slice(0, 160)}`} />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/mentors')} className="flex items-center gap-1.5 text-[#a0a0ab] hover:text-white text-sm mb-6 transition-colors">
          <ChevronLeft size={14} /> Tous les mentors
        </button>

        {/* Warning banner */}
        <div className="flex items-start gap-2 bg-[#ffd93d]/10 border border-[#ffd93d]/20 rounded-xl p-3 mb-6 text-xs text-[#ffd93d]">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <span>Les mentors Kurso sont des facilitateurs bénévoles ou indépendants. Leurs conseils ne remplacent en aucun cas l'avis d'un professionnel qualifié (médecin, juriste, psychologue, thérapeute, comptable, conseiller financier, ou tout autre expert certifié dans son domaine). Kurso agit uniquement comme plateforme de mise en relation et décline toute responsabilité quant aux conseils prodigués par les mentors.</span>
        </div>

        {/* Hero */}
        <div className="kurso-card p-6 mb-6">
          <div className="flex gap-4">
            <div className="w-20 h-20 rounded-full bg-[#ff6b6b]/20 flex items-center justify-center text-[#ff6b6b] text-3xl font-bold flex-shrink-0 overflow-hidden">
              {mentor.avatar ? <img src={mentor.avatar} alt={mentor.name} className="w-full h-full object-cover" /> : mentor.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <h1 className="text-2xl font-heading font-bold text-white">{mentor.name}</h1>
                  <p className="text-[#a0a0ab] text-sm">{EXP_LABELS[mentor.experience_level]} · {mentor.years_experience > 0 ? `${mentor.years_experience} ans d'expérience` : ''}</p>
                </div>
                <button onClick={() => setShowReport(true)} className="text-xs text-[#a0a0ab] hover:text-[#ff6b6b] flex items-center gap-1 transition-colors">
                  <Flag size={12} /> Signaler
                </button>
              </div>
              {mentor.stats?.avg_rating > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  {[1,2,3,4,5].map(s => <Star key={s} size={12} className={s <= Math.round(mentor.stats.avg_rating) ? 'text-[#ffd93d] fill-[#ffd93d]' : 'text-white/20'} />)}
                  <span className="text-[#ffd93d] text-xs ml-1">{mentor.stats.avg_rating?.toFixed(1)} ({mentor.stats.review_count} avis)</span>
                </div>
              )}
            </div>
          </div>

          {mentor.bio && <p className="text-[#e8e8e8] text-sm leading-relaxed mt-4">{mentor.bio}</p>}

          <div className="flex flex-wrap gap-3 mt-4 text-xs text-[#a0a0ab]">
            {mentor.location_city && <span className="flex items-center gap-1"><MapPin size={12} /> {mentor.location_city}{mentor.location_region ? `, ${mentor.location_region}` : ''}</span>}
            <span className="flex items-center gap-1"><Globe size={12} /> {INTERVENTION_LABELS[mentor.intervention_type]}</span>
            {mentor.visio_tool && <span className="flex items-center gap-1"><Video size={12} /> {VISIO_LABELS[mentor.visio_tool]}</span>}
            {mentor.languages?.length > 0 && <span>{mentor.languages.join(', ')}</span>}
          </div>

          {mentor.specialities?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4">
              {mentor.specialities.map(s => <span key={s} className="text-xs bg-[#ff6b6b]/10 text-[#ff6b6b] px-2.5 py-1 rounded-lg">{s}</span>)}
            </div>
          )}

          {mentor.certifications?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs text-[#a0a0ab] mb-2">Certifications & diplômes</p>
              <div className="flex flex-wrap gap-2">
                {mentor.certifications.map(c => <span key={c} className="text-xs bg-white/5 text-[#e8e8e8] px-2.5 py-1 rounded-lg">{c}</span>)}
              </div>
            </div>
          )}
        </div>

        {/* Offers */}
        {mentor.offers?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-heading font-bold text-white mb-3">Offres disponibles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {mentor.offers.map(offer => (
                <div key={offer.id} className="kurso-card p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-white font-semibold text-sm">{offer.title}</h3>
                    <span className={`text-sm font-bold ${offer.price === 0 ? 'text-[#06d6a0]' : 'text-white'}`}>
                      {offer.price === 0 ? 'Gratuit' : `${offer.price}€`}
                    </span>
                  </div>
                  <p className="text-[#a0a0ab] text-xs mb-3 line-clamp-2">{offer.description}</p>
                  <div className="flex items-center gap-3 text-xs text-[#a0a0ab] mb-3">
                    <span className="flex items-center gap-1"><Clock size={10} /> {offer.duration} min</span>
                    <span>{offer.type === 'individuel' ? 'Individuel' : 'Groupe'}</span>
                    <span>{INTERVENTION_LABELS[offer.format]}</span>
                  </div>
                  <button onClick={() => setBookingOffer(offer)}
                    data-testid={`book-offer-${offer.id}`}
                    className="w-full py-2 rounded-xl bg-[#ff6b6b] text-white text-xs font-semibold hover:bg-[#ff5555] transition-colors flex items-center justify-center gap-1.5">
                    <Calendar size={12} /> {offer.price === 0 ? 'Réserver gratuitement' : `Réserver — ${offer.price}€`}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Booking modal */}
        {bookingOffer && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
            <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-white font-bold mb-1">{bookingOffer.title}</h3>
              <p className="text-[#a0a0ab] text-sm mb-4">{bookingOffer.price === 0 ? 'Gratuit' : `${bookingOffer.price}€`} · {bookingOffer.duration} min</p>
              <textarea value={bookMsg} onChange={e => setBookMsg(e.target.value)} placeholder="Message au mentor (optionnel)..." rows={3}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm mb-4 resize-none focus:outline-none" />
              <p className="text-xs text-[#a0a0ab] mb-4">⚠️ Les mentors Kurso sont des facilitateurs bénévoles ou indépendants. Leurs conseils ne remplacent en aucun cas l'avis d'un professionnel qualifié (médecin, juriste, psychologue, thérapeute, comptable, conseiller financier, ou tout autre expert certifié dans son domaine). Kurso agit uniquement comme plateforme de mise en relation et décline toute responsabilité quant aux conseils prodigués par les mentors.</p>
              <div className="flex gap-3">
                <button onClick={() => setBookingOffer(null)} className="flex-1 py-2.5 rounded-xl border border-white/10 text-[#a0a0ab] text-sm">Annuler</button>
                <button onClick={() => handleBook(bookingOffer)} disabled={booking}
                  className="flex-1 py-2.5 rounded-xl bg-[#ff6b6b] text-white text-sm font-bold hover:bg-[#ff5555] disabled:opacity-50">
                  {booking ? 'En cours...' : mentor.offers.find(o => o.id === bookingOffer.id)?.calendly_url ? 'Réserver via Calendly' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reviews */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-heading font-bold text-white">Avis ({reviews.length})</h2>
            {user && <button onClick={() => setShowReview(!showReview)} className="text-sm text-[#ff6b6b] hover:underline">Laisser un avis</button>}
          </div>

          {showReview && (
            <div className="kurso-card p-4 mb-4">
              <div className="flex gap-1 mb-3">
                {[1,2,3,4,5].map(s => (
                  <button key={s} onClick={() => setNewReview(p => ({ ...p, rating: s }))}
                    className={`text-lg ${s <= newReview.rating ? 'text-[#ffd93d]' : 'text-white/20'} transition-colors`}>★</button>
                ))}
              </div>
              <textarea value={newReview.comment} onChange={e => setNewReview(p => ({ ...p, comment: e.target.value }))}
                placeholder="Partagez votre expérience (min 20 caractères)..." rows={3}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm mb-3 resize-none focus:outline-none" />
              <button onClick={handleReview} className="flex items-center gap-1.5 bg-[#ff6b6b] text-white px-4 py-2 rounded-xl text-sm font-semibold">
                <Send size={12} /> Publier
              </button>
            </div>
          )}

          {reviews.length === 0 ? (
            <p className="text-[#a0a0ab] text-sm">Aucun avis pour le moment.</p>
          ) : (
            <div className="space-y-3">
              {reviews.map(r => (
                <div key={r.id} className="kurso-card p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white text-sm font-medium">{r.user_name}</span>
                    <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} size={10} className={s <= r.rating ? 'text-[#ffd93d] fill-[#ffd93d]' : 'text-white/20'} />)}</div>
                  </div>
                  <p className="text-[#a0a0ab] text-xs mb-2">{r.comment}</p>
                  {r.mentor_reply && (
                    <div className="bg-white/5 rounded-xl px-3 py-2 text-xs text-[#e8e8e8]">
                      <span className="text-[#ff6b6b] font-semibold">{mentor.name} : </span>{r.mentor_reply}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Report modal */}
        {showReport && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
            <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-6 max-w-sm w-full">
              <h3 className="text-white font-bold mb-4">Signaler ce mentor</h3>
              <select value={report.reason} onChange={e => setReport(p => ({ ...p, reason: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm mb-3">
                <option value="">Sélectionner un motif...</option>
                <option value="comportement_inapproprie">Comportement inapproprié</option>
                <option value="informations_fausses">Informations fausses</option>
                <option value="contact_hors_plateforme">Tentative de contact hors plateforme</option>
                <option value="conseil_medical_juridique">Conseil médical/juridique non qualifié</option>
                <option value="autre">Autre</option>
              </select>
              <textarea value={report.description} onChange={e => setReport(p => ({ ...p, description: e.target.value }))}
                placeholder="Description détaillée..." rows={3}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm mb-4 resize-none" />
              <div className="flex gap-3">
                <button onClick={() => setShowReport(false)} className="flex-1 py-2 rounded-xl border border-white/10 text-[#a0a0ab] text-sm">Annuler</button>
                <button onClick={handleReport} className="flex-1 py-2 rounded-xl bg-[#ff6b6b] text-white text-sm font-semibold">Envoyer</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
